import React, {
  useEffect,
  useState
} from 'react';

import {
  useParams
} from 'react-router-dom';

const formatDateTime = value => {
  if (!value) return '';

  return new Intl.DateTimeFormat(
    'en-NG',
    {
      timeZone: 'Africa/Lagos',
      dateStyle: 'long',
      timeStyle: 'short'
    }
  ).format(
    new Date(value)
  );
};

export default function OrderConfirmationPublic() {
  const { token } = useParams();

  const [confirmation, setConfirmation] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [acknowledging, setAcknowledging] =
    useState(false);

  const [error, setError] =
    useState('');

  useEffect(() => {
    const previousTitle =
      document.title;

    document.title =
      'Order Confirmation | Bloans Creations Ltd.';

    let robots = document.querySelector(
      'meta[name="robots"]'
    );

    const createdRobots = !robots;

    if (!robots) {
      robots =
        document.createElement('meta');

      robots.setAttribute(
        'name',
        'robots'
      );

      document.head.appendChild(
        robots
      );
    }

    const previousRobots =
      robots.getAttribute('content');

    robots.setAttribute(
      'content',
      'noindex,nofollow,noarchive,nosnippet'
    );

    return () => {
      document.title = previousTitle;

      if (createdRobots) {
        robots.remove();
      } else if (previousRobots) {
        robots.setAttribute(
          'content',
          previousRobots
        );
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          '/api/public/order-confirmations/' +
            encodeURIComponent(token),
          {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
            headers: {
              Accept: 'application/json'
            }
          }
        );

        const payload =
          await response.json();

        if (!response.ok) {
          throw new Error(
            payload.error ||
            'This confirmation is unavailable.'
          );
        }

        if (active) {
          setConfirmation(
            payload.data
          );
        }
      } catch (err) {
        if (active) {
          setError(
            err.message ||
            'This confirmation could not be loaded.'
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [token]);

  const acknowledge = async () => {
    const confirmed = window.confirm(
      'Confirm that you have reviewed your order details?'
    );

    if (!confirmed) return;

    setAcknowledging(true);
    setError('');

    try {
      const response = await fetch(
        '/api/public/order-confirmations/' +
          encodeURIComponent(token) +
          '/acknowledge',
        {
          method: 'POST',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/json'
          },
          body: '{}'
        }
      );

      const payload =
        await response.json();

      if (!response.ok) {
        throw new Error(
          payload.error ||
          'Your acknowledgement could not be recorded.'
        );
      }

      setConfirmation(current => ({
        ...current,
        acknowledged_at:
          payload.data.acknowledged_at
      }));
    } catch (err) {
      setError(
        err.message ||
        'Your acknowledgement could not be recorded.'
      );
    } finally {
      setAcknowledging(false);
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#F2EFE7',
    padding: '24px 12px',
    color: '#3B2A21',
    fontFamily: 'Arial, sans-serif'
  };

  const shellStyle = {
    maxWidth: 820,
    margin: '0 auto'
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={shellStyle}>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 40,
              textAlign: 'center',
              border:
                '1px solid #DED7CB'
            }}
          >
            Loading your order confirmation...
          </div>
        </div>
      </div>
    );
  }

  if (error && !confirmation) {
    return (
      <div style={pageStyle}>
        <div style={shellStyle}>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 40,
              textAlign: 'center',
              border:
                '1px solid #DED7CB'
            }}
          >
            <h2>
              Confirmation unavailable
            </h2>

            <p style={{color: '#70685F'}}>
              {error}
            </p>

            <p style={{fontSize: 13}}>
              Please contact Bloans Creations Ltd.
              on 08028708436.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const expired =
    confirmation.review_expired ||
    new Date() >
      new Date(
        confirmation.review_expires_at
      );

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <div
          style={{
            background: '#3B2A21',
            color: '#F2EFE7',
            padding: '22px 24px',
            borderRadius: '12px 12px 0 0'
          }}
        >
          <div
            style={{
              color: '#A8893D',
              fontWeight: 700,
              fontSize: 22
            }}
          >
            Bloans Creations Ltd.
          </div>

          <div
            style={{
              opacity: 0.85,
              marginTop: 4,
              fontSize: 13
            }}
          >
            Secure Order Confirmation
          </div>
        </div>

        <div
          style={{
            background: '#fff',
            padding: 20,
            borderLeft:
              '1px solid #DED7CB',
            borderRight:
              '1px solid #DED7CB'
          }}
        >
          <div
            style={{
              padding: 14,
              marginBottom: 16,
              borderRadius: 8,
              background:
                expired
                  ? '#FDECEC'
                  : '#F3F7EA',
              border:
                expired
                  ? '1px solid #E8B4B4'
                  : '1px solid #C8D8A8'
            }}
          >
            <strong>
              {expired
                ? 'The 12-hour review period has ended.'
                : 'Your 12-hour review period is active.'}
            </strong>

            <div
              style={{
                marginTop: 6,
                fontSize: 13
              }}
            >
              Review deadline:{" "}
              {formatDateTime(
                confirmation.review_expires_at
              )}
            </div>

            {expired && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 13
                }}
              >
                Contact Bloans immediately if you
                still need assistance. Changes may
                no longer be possible.
              </div>
            )}
          </div>

          {error && (
            <div
              style={{
                padding: 12,
                marginBottom: 16,
                borderRadius: 8,
                color: '#8F2E24',
                background: '#FDECEC'
              }}
            >
              {error}
            </div>
          )}

          <iframe
            title="Order confirmation"
            srcDoc={confirmation.html}
            sandbox=""
            referrerPolicy="no-referrer"
            style={{
              width: '100%',
              height: 720,
              border:
                '1px solid #DED7CB',
              borderRadius: 8,
              background: '#fff'
            }}
          />
        </div>

        <div
          style={{
            background: '#fff',
            border:
              '1px solid #DED7CB',
            borderTop: 0,
            borderRadius:
              '0 0 12px 12px',
            padding: 20,
            textAlign: 'center'
          }}
        >
          {confirmation.acknowledged_at ? (
            <div
              style={{
                padding: 14,
                borderRadius: 8,
                color: '#2E6B3A',
                background: '#EFF7F0',
                fontWeight: 700
              }}
            >
              Order details acknowledged on{" "}
              {formatDateTime(
                confirmation.acknowledged_at
              )}
            </div>
          ) : (
            <>
              <p
                style={{
                  fontSize: 13,
                  color: '#605A54'
                }}
              >
                By continuing, you confirm that you
                have reviewed the order details shown
                above. Acknowledgement does not extend
                the stated review deadline.
              </p>

              <button
                type="button"
                onClick={acknowledge}
                disabled={acknowledging}
                style={{
                  border: 0,
                  borderRadius: 8,
                  padding: '13px 24px',
                  background: '#556B2F',
                  color: '#fff',
                  fontWeight: 700,
                  cursor:
                    acknowledging
                      ? 'wait'
                      : 'pointer'
                }}
              >
                {acknowledging
                  ? 'Recording...'
                  : 'I Have Reviewed My Order'}
              </button>
            </>
          )}

          <p
            style={{
              marginTop: 18,
              color: '#70685F',
              fontSize: 12
            }}
          >
            Need assistance? Call 08028708436 or
            email support@bloanshandbags.com.
          </p>
        </div>
      </div>
    </div>
  );
}
